import type { CharacterGridSpec } from "@/character/grid";
import {
  gmMonsterTemplateToPart,
  hydrateGmMonsterCatalog,
  loadGmMonsterCatalog,
} from "@/gm/gm-monster-catalog";
import { gmPartTemplateToPart, hydrateGmPartCatalog, loadGmPartCatalog } from "@/gm/gm-part-catalog";
import type { CombatRatings } from "@/game/model/character-stats";
import { monsterCombatFromPart, type MonsterCombatConfig } from "@/game/model/monster-stats";
import { normalizePartKind, placementDepth, type HomePartKind } from "@/home/part-kind";
import {
  getTowerFloorConfig,
  hydrateTowerFloorConfigs,
  isActiveTowerFloorConfig,
} from "@/tower/tower-floor-config";
import { TOWER_REPEAT_XP_FACTOR, towerFloorDefinition } from "@/tower/tower-floors";
import { TOWER_DEFAULT_SPAWN_PER_MINUTE } from "@/tower/tower-spawn-rate";
import type { TowerArenaAmbienceResolved } from "@/tower/tower-arena-ambience";

export type TowerEnemySpawn = {
  instanceKey: string;
  monsterId: string;
  name: string;
  imageDataUrl: string;
  spritesheetDataUrl?: string;
  grid?: CharacterGridSpec;
  cellsH: number;
  maxHp: number;
  xpReward: number;
  ratings: CombatRatings;
  /** อัตราเกิด — ตัวต่อนาทีก่อนเกิดตัวถัดไปในคิว */
  spawnPerMinute?: number;
};

export type TowerScenePiece = {
  instanceKey: string;
  imageDataUrl: string;
  gridX: number;
  gridY: number;
  cellsW: number;
  cellsH: number;
  kind: HomePartKind;
  depth: number;
};

export type TowerFloorBattlePlan = {
  floor: number;
  name: string;
  totalXpReward: number;
  spawns: TowerEnemySpawn[];
  customConfig: boolean;
  powerMultiplier: number;
  scenePieces: TowerScenePiece[];
  sceneAmbience?: TowerArenaAmbienceResolved;
};

function scaleMonsterCombat(combat: MonsterCombatConfig, multiplier: number): MonsterCombatConfig {
  const m = multiplier > 0 ? multiplier : 1;
  const ratings: CombatRatings = {
    ...combat.ratings,
    attack: Math.max(1, Math.floor(combat.ratings.attack * m)),
    defense: Math.max(0, Math.floor(combat.ratings.defense * m)),
    dodge: Math.max(0, Math.floor(combat.ratings.dodge * m)),
    hit: Math.max(1, Math.floor(combat.ratings.hit * m)),
  };
  return {
    ...combat,
    maxHp: Math.max(1, Math.floor(combat.maxHp * m)),
    ratings,
  };
}

function buildScenePieces(
  placements: { partId: string; gridX: number; gridY: number }[],
): TowerScenePiece[] {
  const catalog = loadGmPartCatalog();
  const pieces: TowerScenePiece[] = [];
  let idx = 0;
  for (const placement of placements) {
    const template = catalog.find((p) => p.id === placement.partId);
    if (!template) continue;
    const part = gmPartTemplateToPart(template);
    const kind = normalizePartKind(part.kind);
    pieces.push({
      instanceKey: `scene-${idx++}-${part.id}`,
      imageDataUrl: part.imageDataUrl,
      gridX: placement.gridX,
      gridY: placement.gridY,
      cellsW: part.cellsW,
      cellsH: part.cellsH,
      kind,
      depth: placementDepth(
        kind,
        placement.gridY,
        part.cellsH,
        part.depthBias ?? 0,
      ),
    });
  }
  return pieces;
}

function spawnFromTemplate(
  templateId: string,
  index: number,
  name: string,
  imageDataUrl: string,
  maxHp: number,
  xpReward: number,
  ratings: CombatRatings,
  sheet?: { spritesheetDataUrl?: string; grid?: CharacterGridSpec; cellsH?: number },
  spawnPerMinute?: number,
): TowerEnemySpawn {
  return {
    instanceKey: `${templateId || "proc"}-${index}`,
    monsterId: templateId,
    name,
    imageDataUrl,
    spritesheetDataUrl: sheet?.spritesheetDataUrl,
    grid: sheet?.grid,
    cellsH: sheet?.cellsH ?? 1,
    maxHp,
    xpReward,
    ratings,
    spawnPerMinute,
  };
}

export async function buildTowerFloorBattlePlan(floor: number): Promise<TowerFloorBattlePlan> {
  const f = Math.min(100, Math.max(1, Math.floor(floor)));
  await hydrateGmMonsterCatalog();
  await hydrateTowerFloorConfigs();

  const config = getTowerFloorConfig(f);
  await hydrateGmPartCatalog();
  const catalog = loadGmMonsterCatalog();
  const spawns: TowerEnemySpawn[] = [];
  let idx = 0;
  const powerMultiplier = config?.powerMultiplier ?? 1;
  const scenePieces = buildScenePieces(config?.scenePlacements ?? []);

  if (config && isActiveTowerFloorConfig(config)) {
    for (const row of config.spawns) {
      const template = catalog.find((m) => m.id === row.monsterId);
      if (!template) continue;
      const part = gmMonsterTemplateToPart(template);
      const baseCombat = monsterCombatFromPart(part);
      if (!baseCombat) continue;
      const rowPower = row.powerMultiplier ?? 1;
      const combat = scaleMonsterCombat(baseCombat, powerMultiplier * rowPower);
      const ppm = row.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE;
      for (let i = 0; i < row.count; i += 1) {
        spawns.push(
          spawnFromTemplate(
            template.id,
            idx++,
            template.name,
            template.imageDataUrl,
            combat.maxHp,
            combat.xpReward,
            combat.ratings,
            {
              spritesheetDataUrl: template.spritesheetDataUrl,
              grid: template.grid,
              cellsH: template.cellsH,
            },
            ppm,
          ),
        );
      }
    }
  }

  if (spawns.length === 0) {
    const def = towerFloorDefinition(f);
    spawns.push(
      spawnFromTemplate(
        "",
        0,
        def.name,
        "",
        def.maxHp,
        def.xpReward,
        def.ratings,
      ),
    );
    return {
      floor: f,
      name: def.name,
      totalXpReward: def.xpReward,
      spawns,
      customConfig: false,
      powerMultiplier: 1,
      scenePieces,
    };
  }

  const totalXpReward = spawns.reduce((sum, s) => sum + s.xpReward, 0);
  const name = f % 10 === 0 ? `บอสชั้น ${f}` : `ชั้น ${f}`;
  return {
    floor: f,
    name,
    totalXpReward: Math.max(1, totalXpReward),
    spawns,
    customConfig: true,
    powerMultiplier,
    scenePieces,
  };
}

type TowerFloorStatBlock = {
  floor: number;
  name: string;
  monsterCount: number;
  totalHp: number;
  baseXp: number;
  source: "gm" | "default";
};

function towerFloorStatBlock(floor: number): TowerFloorStatBlock {
  const f = Math.min(100, Math.max(1, Math.floor(floor)));
  const config = getTowerFloorConfig(f);
  const catalog = loadGmMonsterCatalog();
  if (config && isActiveTowerFloorConfig(config)) {
    const mult = config.powerMultiplier ?? 1;
    let count = 0;
    let totalHp = 0;
    let totalXp = 0;
    for (const row of config.spawns) {
      const template = catalog.find((m) => m.id === row.monsterId);
      if (!template) continue;
      const part = gmMonsterTemplateToPart(template);
      const baseCombat = monsterCombatFromPart(part);
      if (!baseCombat) continue;
      const rowPower = row.powerMultiplier ?? 1;
      const combat = scaleMonsterCombat(baseCombat, mult * rowPower);
      count += row.count;
      totalHp += combat.maxHp * row.count;
      totalXp += combat.xpReward * row.count;
    }
    if (count > 0) {
      return {
        floor: f,
        name: f % 10 === 0 ? `บอสชั้น ${f}` : `ชั้น ${f}`,
        monsterCount: count,
        totalHp,
        baseXp: Math.max(1, totalXp),
        source: "gm",
      };
    }
  }
  const def = towerFloorDefinition(f);
  return {
    floor: f,
    name: def.name,
    monsterCount: 1,
    totalHp: def.maxHp,
    baseXp: def.xpReward,
    source: "default",
  };
}

export type TowerFloorSidebarMeta = {
  name: string;
  line1: string;
  line2: string;
  baseXp: number;
};

export function towerFloorSidebarMeta(floor: number): TowerFloorSidebarMeta {
  const block = towerFloorStatBlock(floor);
  if (block.source === "gm") {
    const config = getTowerFloorConfig(block.floor);
    const mult = config?.powerMultiplier ?? 1;
    const multLabel = mult !== 1 ? ` · พลัง ×${mult}` : "";
    const sceneCount = config?.scenePlacements?.length ?? 0;
    const sceneLabel = sceneCount > 0 ? ` · ฉาก ${sceneCount} ชิ้น` : "";
    return {
      name: block.name,
      line1: `มอนสเตอร์ ${block.monsterCount} ตัว · HP รวม ${block.totalHp.toLocaleString("en-US")}${multLabel}${sceneLabel}`,
      line2: `EXP พื้นฐาน ${block.baseXp.toLocaleString("en-US")} (ครั้งแรกเต็ม · ซ้ำ 70%)`,
      baseXp: block.baseXp,
    };
  }
  return {
    name: block.name,
    line1: `HP ${block.totalHp.toLocaleString("en-US")} (มอนสเตอร์เริ่มต้น)`,
    line2: `EXP พื้นฐาน ${block.baseXp.toLocaleString("en-US")} (ครั้งแรกเต็ม · ซ้ำ 70%)`,
    baseXp: block.baseXp,
  };
}

export type TowerFloorLobbyView = TowerFloorStatBlock & {
  firstClear: boolean;
  xpFirst: number;
  xpRepeat: number;
  progressPct: number;
};

export function towerFloorLobbyView(
  floor: number,
  highestCleared: number,
  maxFloor: number,
): TowerFloorLobbyView {
  const block = towerFloorStatBlock(floor);
  const firstClear = block.floor > highestCleared;
  return {
    ...block,
    firstClear,
    xpFirst: block.baseXp,
    xpRepeat: Math.floor(block.baseXp * TOWER_REPEAT_XP_FACTOR),
    progressPct: maxFloor > 0 ? Math.round((highestCleared / maxFloor) * 100) : 0,
  };
}
